<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ variationOrder?.status === VariationOrderStatus.principled ? $t('HeadContract.VariationsSchedule.RegisterPricingModal.title.approvePricing', { code: variationOrder?.code }) : $t('HeadContract.VariationsSchedule.RegisterPricingModal.title.registerPricing', { code: variationOrder?.code }) }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="min-w-md flex flex-col overflow-y-auto w-full">
        <p class="font-semibold text-lg">
          {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.variationOrder') }}
        </p>

        <p class="font-semibold">
          {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.description') }}
        </p>

        <p>
          {{ variationOrder.description }}
        </p>

        <p
          v-if="variationOrder.notes"
          class="font-semibold mt-2"
        >
          {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.notes') }}
        </p>

        <div
          v-if="variationOrder.notes"
        >
          {{ variationOrder.notes }}
        </div>

        <form
          action=""
          class="flex flex-col space-y-4 mt-4"
          @submit.prevent=""
        >
          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.label.dateReceived') }}

            <AppInput
              v-model="pricingForm.form.date_received"
              name="date_received"
              type="date"
              class="form-input"
              :error="pricingForm.getErrors('date_received')"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.label.to') }}

            <AppInput
              v-model="pricingForm.form.to"
              name="to"
              type="text"
              class="form-input"
              :error="pricingForm.getErrors('to')"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.label.from') }}

            <AppInput
              v-model="pricingForm.form.from"
              name="from"
              class="form-input"
              type="text"
              :error="pricingForm.getErrors('from')"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.label.description') }}

            <textarea
              v-model="pricingForm.form.description"
              name="description"
              class="form-input resize-none"
              type="text"
            />

            <span
              v-if="pricingForm.getErrors('description')"
              class="text-red-600 text-sm"
            >{{ pricingForm.getErrors('description') }}</span>
          </label>

          <label class="flex justify-between">
            {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.label.responseRequired') }}

            <input
              v-model="pricingForm.form.response_required"
              name="response_required"
              type="checkbox"
              class="form-checkbox"
              true-value="1"
              false-value="0"
            >
          </label>

          <label class="mt-4">
            {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.label.pricing') }} <span class="text-sm text-gray-400">({{ $t('currency.symbol') }})</span>

            <AppCurrencyInput
              v-model="pricingForm.form.budget"
              name="budget"
              class="form-input mt-2"
              :error="pricingForm.getErrors('budget')"
            />
          </label>

          <FileUpload
            v-model="pricingForm.form.media"
            multiple
            @change="addFile"
          />

          <div
            v-if="filesUploaded.length"
            class="overflow-x-auto max-w-sm flex"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="pricingForm.getErrors(`media.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          data-test="apply-filter"
          @click="savePricingNotice"
        >
          {{ $t('HeadContract.VariationsSchedule.RegisterPricingModal.button.save') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput, falsey } from '@/helpers';
import { VariationOrderStatus } from '@/models/VariationOrder';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const emit = defineEmits<{
  (e: 'close');
  (e: 'priced');
}>();

const props = defineProps<{
  variationOrderId: Id;
}>();

const variationsOrdersStore = useVariationOrdersStore();

const variationOrder = computed(() => {
  return variationsOrdersStore.models.with('trade_items').find(props.variationOrderId);
});

const pricingForm = useFormData({
  budget: variationOrder.value?.totalBudget ?? 0,
  date_received: dateForDateInput(new Date()),
  to: '',
  from: '',
  description: '',
  response_required: 0,
  media: [],
});

const variationOrderPricingAction = useStoreApiAction(variationsOrdersStore.storePricingNotice);

const savePricingNotice = () => {
  variationOrderPricingAction.request(props.variationOrderId, pricingForm.toFormDataObject()).then((data) => {
    Swal.fire('Variation Priced', '', 'success').then(() => {
      emit('priced');
      emit('close');
    });
  }).catch((error) => {
    pricingForm.setErrors(error.data);
    console.log(error);
  });
};

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  pricingForm.form.media.splice(index, 1);
});
</script>

<style scoped></style>
